import React, { useState } from "react";
import PropTypes from "prop-types";
import { useQuery } from "react-query";
import { request } from "../../config/helpers/axios-instance";
import Loader from "../Loader";

const RANGE_OPTIONS = [{ label: "24 hours", days: 1 }, { label: "7 days", days: 7 }, { label: "30 days", days: 30 }];
const PAGE_SIZE = 20;

const formatDateTime = (value) => new Date(value).toLocaleString([], { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });

function Pill({ count, label, tone }) {
    if (!count) return null;
    return <span className={`px-2 py-0.5 rounded-md text-[11px] font-semibold ${tone}`}>{count} {label}</span>;
}

Pill.propTypes = {
    count: PropTypes.number,
    label: PropTypes.string.isRequired,
    tone:  PropTypes.string.isRequired,
};
Pill.defaultProps = { count: 0 };

export default function CartList({ onSelect }) {
    const [days, setDays] = useState(7);
    const [page, setPage] = useState(1);

    const { data, isFetching, isError } = useQuery(
        ["pricing-cart-list", days, page],
        () => request({
            url: `ops/pricing/carts?page=${page}&pageSize=${PAGE_SIZE}&from=${encodeURIComponent(new Date(Date.now() - days * 86400000).toISOString())}`,
            method: "GET",
        }),
        { refetchOnWindowFocus: false, keepPreviousData: true }
    );
    const result = data?.data?.data;
    const carts = result?.carts ?? [];
    const pageCount = result ? Math.max(1, Math.ceil(result.total / PAGE_SIZE)) : 1;

    return (
        <div className="bg-white rounded-2xl shadow-soft-xl p-5 flex flex-col gap-4">
            <div className="flex items-center justify-between">
                <div>
                    <h2 className="text-lg font-semibold font-sans text-gray-800">Carts</h2>
                    <p className="text-xs font-sans text-gray-400">Most recently priced first. Click a cart to see its full analysis.</p>
                </div>
                <div className="flex gap-2">
                    {RANGE_OPTIONS.map((option) => (
                        <button
                            key={option.days}
                            onClick={() => { setDays(option.days); setPage(1); }}
                            className={`px-3 py-1 rounded-lg text-xs font-semibold font-sans ${days === option.days ? "bg-[#069079] text-white" : "bg-gray-100 text-gray-600"}`}
                        >
                            {option.label}
                        </button>
                    ))}
                </div>
            </div>

            {isFetching && !result && <div className="flex justify-center py-8"><Loader size="large" text="Loading carts…" /></div>}
            {isError && <p className="text-sm text-gray-400 font-sans text-center py-6">Could not load the carts.</p>}
            {result && carts.length === 0 && <p className="text-sm text-gray-400 font-sans text-center py-6">No priced carts in this period.</p>}

            {carts.length > 0 && (
                <div className="overflow-x-auto">
                    <table className="w-full text-sm font-sans">
                        <thead>
                            <tr className="text-left text-gray-500 border-b-2 border-gray-100 h-10">
                                <th className="pl-3 font-semibold">Cart</th>
                                <th className="font-semibold">Priced at</th>
                                <th className="font-semibold">Prescription</th>
                                <th className="font-semibold">Lines</th>
                                <th className="font-semibold">Outcome</th>
                                <th className="font-semibold">Total</th>
                            </tr>
                        </thead>
                        <tbody>
                            {carts.map((cart) => (
                                <tr
                                    key={cart.cartId}
                                    onClick={() => onSelect(cart.cartId)}
                                    className="border-t border-gray-100 h-12 hover:bg-gray-50 cursor-pointer"
                                >
                                    <td className="pl-3 font-semibold text-[#069079]">#{cart.cartId}</td>
                                    <td className="text-gray-600">{formatDateTime(cart.pricedAt)}</td>
                                    <td className="text-gray-500">{cart.prescriptionId ? `#${cart.prescriptionId}` : "—"}</td>
                                    <td className="text-gray-700">{cart.lines}</td>
                                    <td>
                                        <div className="flex gap-1 flex-wrap">
                                            <Pill count={cart.readyCount} label="READY" tone="bg-green-100 text-green-700" />
                                            <Pill count={cart.failedCount} label="FAILED" tone="bg-red-100 text-red-700" />
                                            <Pill count={cart.reviewCount} label="REVIEW" tone="bg-yellow-100 text-yellow-700" />
                                        </div>
                                    </td>
                                    <td className="font-semibold text-gray-800">{cart.cartTotal ? `₹${cart.cartTotal}` : "—"}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {result && pageCount > 1 && (
                <div className="flex items-center justify-end gap-3 text-xs font-sans text-gray-500">
                    <button disabled={page <= 1} onClick={() => setPage(page - 1)} className="px-3 py-1 rounded-lg bg-gray-100 disabled:opacity-40">Previous</button>
                    <span>Page {page} of {pageCount}</span>
                    <button disabled={page >= pageCount} onClick={() => setPage(page + 1)} className="px-3 py-1 rounded-lg bg-gray-100 disabled:opacity-40">Next</button>
                </div>
            )}
        </div>
    );
}

CartList.propTypes = { onSelect: PropTypes.func.isRequired };
